// Contexto de navegación de validación (persistido en sessionStorage).
// Permite que el detalle sepa: a qué vista volver (lista/kanban) preservando
// filtros, y cuál es el "siguiente" servicio por validar.
const KEY = "sv2:navctx"

// ctx = {
//   view: "list" | "kanban",
//   returnUrl: string,          // ruta + query para reconstruir la vista
//   items: [{ service_code, validated }],  // SOLO detallables (FINISHED); se usa
//                               //   para prev/next (saltar a servicios validables)
//   order: [service_code],      // opcional; TODOS los service_code de la página
//                               //   del backend, en su orden real y SIN filtrar
//                               //   por detallable (solo modo plano). Es el
//                               //   universo con el que `total` (resp.total)
//                               //   comparte base para el contador "N/total".
//   mode: "flat" | "grouped",   // opcional; "flat" solo en modo lista plana
//   page: number,               // opcional; página del backend (solo modo plano)
//   size: number,               // opcional; tamaño de página (solo modo plano)
//   total: number,              // opcional; total de la lista filtrada (el "283",
//                               //   solo modo plano) — resp.total del MISMO request
// }
// Los campos order/mode/page/size/total solo se escriben en modo lista plana; en
// los modos agrupados (empresa/estatus/Uber) mode = "grouped" y no hay
// order/page/size/total.
// El detalle solo existe para servicios finalizados (con snapshot de validación).
export const isDetailAvailable = (s) =>
  !!s?.service_code && String(s.status || s.trip_status || "").toUpperCase() === "FINISHED"

export const saveNavContext = (ctx) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(ctx))
  } catch (_e) {
    /* no-op */
  }
}

export const getNavContext = () => {
  try {
    const raw = sessionStorage.getItem(KEY)
    return raw ? JSON.parse(raw) : null
  } catch (_e) {
    return null
  }
}

// Devuelve el siguiente service_code por validar después de `code` (prioriza
// no validados). Si no hay contexto o no encuentra siguiente, devuelve null.
export const getNextPendingCode = (code) => {
  const ctx = getNavContext()
  const items = ctx?.items || []
  if (!items.length) return null
  const idx = items.findIndex((it) => it.service_code === code)
  const start = idx >= 0 ? idx + 1 : 0
  // 1) siguiente no validado a partir de la posición actual
  for (let i = start; i < items.length; i += 1) {
    if (!items[i].validated) return items[i].service_code
  }
  // 2) cualquier siguiente (aunque validado), para poder recorrer
  if (idx >= 0 && idx + 1 < items.length) return items[idx + 1].service_code
  return null
}

// Devuelve el service_code ANTERIOR a `code` en el orden visible (secuencia de
// navegación). Sirve para que el botón "Atrás" regrese al servicio previo en
// vez de a la lista. Devuelve null si no hay contexto o `code` es el primero.
export const getPrevCode = (code) => {
  const ctx = getNavContext()
  const items = ctx?.items || []
  if (!items.length) return null
  const idx = items.findIndex((it) => it.service_code === code)
  if (idx > 0) return items[idx - 1].service_code
  return null
}

// Posición del `code` para el contador "N/total" del detalle (F5).
// - Modo "flat" (lista plana): índice GLOBAL 1-based dentro de la lista filtrada
//   `(page-1)*size + idxEnPagina + 1`, con total = total REAL de la lista
//   filtrada (`ctx.total`, el "283"). INVARIANTE CRÍTICA (mismo universo): el
//   índice en la página se calcula sobre `ctx.order` (TODOS los service_code de
//   la página del backend, en su orden real y SIN filtrar por detallable), NO
//   sobre `ctx.items` (que está pre-filtrado a FINISHED para prev/next). Así
//   numerador y `ctx.total` (resp.total del MISMO request, que cuenta todos los
//   estatus) comparten base aunque el filtro de estatus admita filas no FINISHED.
// - Modo "grouped", sin `ctx.order` o sin esos datos (recarga directa, code
//   ausente, sessionStorage no disponible): devuelve null → el contador se
//   OCULTA. No se mezcla un índice de un universo con un total de otro.
export const getPositionInContext = (code) => {
  const ctx = getNavContext()
  if (!ctx || ctx.mode !== "flat" || !Number.isFinite(ctx.total)) return null
  // El índice DEBE salir del orden real de la página del backend (ctx.order),
  // mismo universo que ctx.total. Sin `order` no se puede garantizar → oculta.
  const order = Array.isArray(ctx.order) ? ctx.order : null
  if (!order || !order.length) return null
  const idxInPage = order.indexOf(code)
  if (idxInPage < 0) return null
  const page = Number(ctx.page) || 1
  const size = Number(ctx.size) || order.length
  return { index: (page - 1) * size + idxInPage + 1, total: ctx.total }
}

// Serializa filtros a query string (omite vacíos).
export const filtersToQuery = (filters = {}, extra = {}) => {
  const p = new URLSearchParams()
  Object.entries({ ...filters, ...extra }).forEach(([k, v]) => {
    if (v !== "" && v != null) p.set(k, v)
  })
  const qs = p.toString()
  return qs ? `?${qs}` : ""
}
